import type { FC } from "react";
import { useEffect } from "react";

import { Alert } from "@Components/Icons/Alert";
import type { IIconBase } from "@Components/Icons/IconBase";
import type { IImageLoader } from "@Components/ImageLoader";
import ImageLoader from "@Components/ImageLoader";
import { requestTrack } from "@Components/Modules/Helpers";

import type { IButtonsBar } from "./ButtonsBar";
import { ButtonsBar } from "./ButtonsBar";
import { Success as SuccessIcon } from "./icons/Success";

import * as css from "./UserCommunicationTemplate.module.css";

export interface IUserCommunicationButton {
    text: string;
    telemetryClickName?: string;
    buttonActionType: IActionType;
}

export interface IUserCommunicationTemplate {
    displayType: DisplayType;
    title?: string;
    message?: string;
    buttons?: IUserCommunicationButton[];
    eventId?: string;
    ajaxTrackingAction?: string;
    image: IImageLoader;
    onBack?: () => void;
    onExit?: () => void;
    onNext?: () => void;
    onCustomAction1?: () => void;
    onCustomAction2?: () => void;
}

export enum DisplayType {
    Undefined,
    PlainInfo,
    Success,
    Fail,
    DialogPopup,
    Info,
}

export enum IActionType {
    Undefined = 0,
    CommonNext = 1,
    CommonExit = 2,
    CommonBack = 3,
    CommonContact = 4,
    UsprflOffersOpen = 5,
}

const UserCommunicationTemplate: FC<IUserCommunicationTemplate> = (props) => {
    useEffect(() => requestTrack(props.ajaxTrackingAction), []);

    const renderButtonsBar = () => {
        if (!props.buttons) {
            return null;
        }

        const buttonCustom = props.buttons.map((buttonItem) => {
            let action: any = null;
            switch (buttonItem.buttonActionType) {
                case IActionType.CommonBack:
                    action = props.onBack;
                    break;
                case IActionType.CommonExit:
                    action = props.onExit;
                    break;
                case IActionType.CommonNext:
                    action = props.onNext;
                    break;
                case IActionType.CommonContact:
                    action = props.onCustomAction2;
                    break;
                case IActionType.UsprflOffersOpen:
                    action = props.onCustomAction1;
                    break;
                default:
                    break;
            }

            return {
                text: buttonItem.text,
                trackingAction: buttonItem.telemetryClickName,
                onClick: action,
            };
        });

        const buttonsBar: IButtonsBar = {
            nextButton: buttonCustom[0],
            cancelButton: buttonCustom[1],
        };

        return <ButtonsBar {...buttonsBar} />;
    };

    const icon = () => {
        let ComponentWrapper: React.ComponentClass<IIconBase> | null = null;
        let iconAppearance: "DarkBlue" | "Green" = "DarkBlue";
        switch (props.displayType) {
            case DisplayType.PlainInfo:
            case DisplayType.Undefined:
            case DisplayType.DialogPopup:
                return null;
            case DisplayType.Success:
                ComponentWrapper = SuccessIcon;
                iconAppearance = "Green";
                break;
            case DisplayType.Fail:
                ComponentWrapper = Alert;
                break;
            case DisplayType.Info:
                return (
                    <div className={css.imageContainer}>
                        <ImageLoader {...props.image} />
                    </div>
                );
            default:
                return null;
        }

        return (
            <div className={css.iconItem}>
                {ComponentWrapper && <ComponentWrapper iconAppearance={iconAppearance} />}
            </div>
        );
    };

    return (
        <div className={css.userCommunicationTemplateComponent}>
            <div className={css.innerContent}>
                {icon()}
                {props.title && (
                    <h2 className={css.title} dangerouslySetInnerHTML={{ __html: props.title }} />
                )}
                <br />
                {props.message && (
                    <p className={css.message} dangerouslySetInnerHTML={{ __html: props.message }} />
                )}
            </div>
            {renderButtonsBar()}
        </div>
    );
};

export default UserCommunicationTemplate;
